<template>
  <div
    class="relative flex items-start min-h-screen w-full max-w-full overflow-x-clip bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950"
  >
    <!-- Sidebar -->
    <AdminSidebar
      :is-collapsed="isSidebarCollapsed"
      :is-open-mobile="isSidebarOpenMobile"
      :sidebar-search="sidebarSearch"
      :lang="lang"
      :t="t"
      @update:sidebar-search="sidebarSearch = $event"
      @update:is-open-mobile="isSidebarOpenMobile = $event"
    />

    <div class="flex flex-col flex-1 min-w-0 w-full">
      <AdminTopbar
        :is-collapsed="isSidebarCollapsed"
        :lang="lang"
        :t="t"
        @toggle-sidebar="toggleSidebar"
      />

      <main class="flex-1 px-4 py-6 md:px-6 lg:px-8">
        <div class="w-full mx-auto space-y-5 max-w-7xl">
          <!-- Header -->
          <div
            class="relative p-4 overflow-hidden border shadow-sm rounded-2xl bg-white/80 dark:bg-slate-900/80 border-slate-200/80 dark:border-slate-700/80 backdrop-blur"
          >
            <div
              class="pointer-events-none absolute -top-12 -end-10 w-40 h-40 rounded-[60px] bg-secondary/20 blur-2xl"
            ></div>

            <div class="relative z-10 flex items-center justify-between gap-4">
              <div>
                <h1
                  class="text-lg font-bold text-slate-900 md:text-xl dark:text-white"
                >
                  {{ t("my_invoices") || "My Invoices" }}
                </h1>
                <p class="text-sm text-slate-500 dark:text-slate-400">
                  {{
                    t("my_invoices_hint") ||
                    "Track your invoices and payment status."
                  }}
                </p>

                <!-- quick chips -->
                <div class="flex flex-wrap gap-2 mt-3 text-[11px]">
                  <span
                    class="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"
                  >
                    <i class="mdi mdi-receipt text-[15px]"></i>
                    {{ invoices.length }} {{ t("invoices") || "Invoices" }}
                  </span>

                  <span
                    class="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300"
                  >
                    <i class="mdi mdi-check-circle-outline text-[15px]"></i>
                    {{ paidCount }} {{ t("paid") || "Paid" }}
                  </span>

                  <span
                    class="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300"
                  >
                    <i class="mdi mdi-timer-sand text-[15px]"></i>
                    {{ pendingCount }} {{ t("pending") || "Pending" }}
                  </span>
                </div>
              </div>

              <div class="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  class="px-3 py-2 text-xs font-medium bg-white border rounded-lg dark:bg-slate-900 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                  @click="fetchInvoices"
                  :disabled="loading"
                >
                  <i class="mdi mdi-refresh text-[16px]"></i>
                  {{
                    loading
                      ? t("loading") || "Loading..."
                      : t("refresh") || "Refresh"
                  }}
                </button>
              </div>
            </div>
          </div>

          <!-- Filters -->
          <div
            class="p-4 border shadow-sm rounded-2xl bg-white/80 dark:bg-slate-900/80 border-slate-200 dark:border-slate-700 backdrop-blur"
          >
            <div
              class="grid grid-cols-1 gap-3 md:grid-cols-[220px_220px_1fr] items-end"
            >
              <div class="space-y-1">
                <label
                  class="text-[11px] font-semibold text-slate-600 dark:text-slate-300"
                >
                  {{ t("status") || "Status" }}
                </label>
                <select
                  v-model="filters.status"
                  class="w-full h-10 px-3 text-sm border rounded-xl bg-white/70 dark:bg-slate-900/70 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="">{{ t("all") || "All" }}</option>
                  <option value="paid">{{ t("paid") || "Paid" }}</option>
                  <option value="unpaid">
                    {{ t("pending") || "Pending" }}
                  </option>
                  <option value="cancelled">
                    {{ t("reject") || "Cancelled" }}
                  </option>
                </select>
              </div>

              <div class="space-y-1">
                <label
                  class="text-[11px] font-semibold text-slate-600 dark:text-slate-300"
                >
                  {{ t("payment_method") || "Payment Method" }}
                </label>
                <select
                  v-model="filters.method"
                  class="w-full h-10 px-3 text-sm border rounded-xl bg-white/70 dark:bg-slate-900/70 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  <option value="">{{ t("all") || "All" }}</option>
                  <option value="cash">{{ t("cash") || "Cash" }}</option>
                  <option value="bank_transfer">
                    {{ t("bank_transfer") || "Bank Transfer" }}
                  </option>
                </select>
              </div>

              <div class="space-y-1">
                <label
                  class="text-[11px] font-semibold text-slate-600 dark:text-slate-300"
                >
                  {{ t("search") || "Search" }}
                </label>
                <div class="relative">
                  <i
                    class="mdi mdi-magnify absolute top-1/2 -translate-y-1/2 text-[18px] text-slate-400"
                    :class="lang === 'ar' ? 'right-3' : 'left-3'"
                  ></i>
                  <input
                    v-model="filters.q"
                    type="text"
                    class="w-full h-10 text-sm border rounded-xl bg-white/70 dark:bg-slate-900/70 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/30"
                    :class="lang === 'ar' ? 'pr-10 pl-3' : 'pl-10 pr-3'"
                    :placeholder="
                      t('search_by_invoice_or_consultation') ||
                      'Search by invoice # or consultation #'
                    "
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- List -->
          <div v-if="loading" class="py-10 text-center text-slate-500">
            {{ t("loading") || "Loading..." }}
          </div>
          <div v-else>
            <div
              class="overflow-hidden border rounded-2xl bg-white/80 dark:bg-slate-900/80 border-slate-200 dark:border-slate-700"
            >
              <div class="overflow-auto">
              <table class="min-w-full text-sm">
  <thead class="bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-200">
    <tr class="text-center">
      <th class="px-4 py-3">{{ t("invoice") || "Invoice" }}</th>

      <!-- Client: خليها يسار (أو يمين بالعربي) -->
      <th class="px-4 py-3" :class="lang === 'ar' ? 'text-right' : 'text-left'">
        {{ t("client_info") || "Client" }}
      </th>

      <th class="px-4 py-3">{{ t("date") || "Issued" }}</th>
      <th class="px-4 py-3">{{ t("consult_table_date") || "Due" }}</th>
      <th class="px-4 py-3">{{ t("status") || "Status" }}</th>
      <th class="px-4 py-3">{{ t("total_amount") || "Total" }}</th>
      <th class="px-4 py-3">{{ t("consult_table_actions") || "Actions" }}</th>
    </tr>
  </thead>

  <tbody>
    <tr
      v-for="inv in filteredInvoices"
      :key="inv.id"
      class="text-center border-t border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100"
    >
      <td class="px-4 py-3 font-mono">{{ inv.number }}</td>

      <!-- Client: نفس محاذاة الهيدر -->
      <td class="px-4 py-3" :class="lang === 'ar' ? 'text-right' : 'text-left'">
        <div class="font-semibold truncate max-w-[260px]">
          {{ inv.client_name || "—" }}
        </div>
        <div class="text-xs text-slate-500 truncate max-w-[260px]">
          {{ inv.client_email || inv.client_phone || "—" }}
        </div>
      </td>

      <td class="px-4 py-3">{{ inv.issued_at || "—" }}</td>
      <td class="px-4 py-3">{{ inv.due_at || "—" }}</td>

      <td class="px-4 py-3">
        <span class="inline-flex items-center justify-center px-2 py-1 text-[11px] rounded-lg" :class="statusChip(inv.status)">
          {{ statusLabel(inv.status) }}
        </span>
      </td>

      <td class="px-4 py-3 font-bold text-emerald-600 whitespace-nowrap">
        {{ Number(inv.total || 0).toFixed(2) }} SAR
      </td>

      <!-- ✅ Actions -->
      <td class="px-4 py-3">
        <div class="flex items-center justify-center gap-2">
          <button
            type="button"
            class="inline-flex items-center justify-center gap-1 px-3 text-xs font-semibold border rounded-lg h-9 border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
            @click="openInvoiceModal(inv)"
          >
            <i class="mdi mdi-eye-outline"></i>
            {{ t("view") || "View" }}
          </button>

          <button
            v-if="inv.pdf_url"
            type="button"
            class="inline-flex items-center justify-center gap-1 px-3 text-xs font-semibold text-white rounded-lg h-9 bg-primary hover:bg-primary/90"
            @click="openUrl(inv.pdf_url)"
          >
            <i class="mdi mdi-download-outline"></i>
            {{ t("view") || "Download" }}
          </button>
        </div>
      </td>
    </tr>

    <tr v-if="!filteredInvoices.length">
      <td colspan="7" class="px-4 py-8 text-center text-slate-500">
        {{ t("no_invoices") || "No invoices found." }}
      </td>
    </tr>
  </tbody>
</table>

              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- ✅ Invoice Modal (same style as your accept modal) -->
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-0 bg-black/50"
      style="margin-top: 0 !important"
      @click.self="closeModal"
    >
      <div
        class="w-full max-w-xl overflow-hidden border shadow-xl rounded-2xl bg-white/95 dark:bg-slate-900/95 border-slate-200 dark:border-slate-700 backdrop-blur"
      >
        <!-- header -->
        <div
          class="flex items-start justify-between gap-3 p-4 border-b border-slate-200 dark:border-slate-700"
        >
          <div class="flex items-start gap-3">
            <div
              class="flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600"
            >
              <i class="mdi mdi-receipt text-[20px]"></i>
            </div>
            <div>
              <div class="text-sm font-bold text-slate-900 dark:text-white">
                {{ t("invoice") || "Invoice" }} #{{ selected?.number }}
              </div>
              <div class="text-xs text-slate-500 dark:text-slate-400">
                {{ t("payment_details") || "Payment details" }}
              </div>
            </div>
          </div>

          <button
            class="inline-flex items-center justify-center w-9 h-9 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
            @click="closeModal"
            :title="t('close') || 'Close'"
          >
            <i
              class="mdi mdi-close text-[18px] text-slate-600 dark:text-slate-200"
            ></i>
          </button>
        </div>

        <!-- body -->
        <div class="p-4 space-y-4">
          <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div
              class="p-3 border rounded-xl border-slate-200 dark:border-slate-700"
            >
              <div class="text-[11px] text-slate-500">
                {{ t("date") || "Date" }}
              </div>
              <div class="text-sm font-semibold text-slate-900 dark:text-white">
                {{ selected?.date || "—" }}
              </div>
            </div>
            <div
              class="p-3 border rounded-xl border-slate-200 dark:border-slate-700"
            >
              <div class="text-[11px] text-slate-500">
                {{ t("payment_method") || "Payment Method" }}
              </div>
              <div class="text-sm font-semibold text-slate-900 dark:text-white">
                {{
                  selected?.method === "cash"
                    ? t("cash") || "Cash"
                    : t("bank_transfer") || "Bank Transfer"
                }}
              </div>
            </div>
          </div>

          <div
            class="p-4 border rounded-2xl border-slate-200 dark:border-slate-700"
          >
            <div class="flex items-center justify-between text-xs">
              <span class="text-slate-500">{{
                t("offer_before_tax") || "Before tax"
              }}</span>
              <span class="font-semibold text-slate-900 dark:text-white"
                >{{ Number(selected?.before || 0).toFixed(2) }} SAR</span
              >
            </div>
            <div class="flex items-center justify-between mt-2 text-xs">
              <span class="text-slate-500">{{
                t("tax_percent") || "Tax %"
              }}</span>
              <span class="font-semibold text-slate-900 dark:text-white"
                >{{ Number(selected?.tax_percent || 0) }}%</span
              >
            </div>
            <div class="flex items-center justify-between mt-2 text-xs">
              <span class="text-slate-500">{{
                t("tax_amount") || "Tax amount"
              }}</span>
              <span class="font-semibold text-slate-900 dark:text-white"
                >{{ Number(selected?.tax_amount || 0).toFixed(2) }} SAR</span
              >
            </div>

            <div
              class="flex items-center justify-between pt-3 mt-3 border-t border-slate-200 dark:border-slate-700"
            >
              <span
                class="text-sm font-semibold text-slate-700 dark:text-slate-200"
                >{{ t("total_amount") || "Total" }}</span
              >
              <span class="text-lg font-bold text-emerald-600"
                >{{ Number(selected?.total || 0).toFixed(2) }} SAR</span
              >
            </div>
          </div>

          <div
            class="p-4 border rounded-2xl border-slate-200 dark:border-slate-700"
          >
            <div class="flex items-center justify-between">
              <span class="text-[11px] text-slate-500">{{
                t("status") || "Status"
              }}</span>
              <span
                class="px-2 py-1 text-[11px] rounded-lg"
                :class="statusChip(selected?.status)"
              >
                {{ statusLabel(selected?.status) }}
              </span>
            </div>

            <div
              v-if="selected?.method === 'bank_transfer'"
              class="mt-3 text-xs text-slate-600 dark:text-slate-300"
            >
              <div v-if="selected?.bank_account">
                <div class="font-semibold">
                  {{ selected.bank_account.bank_name }} —
                  {{ selected.bank_account.account_name }}
                </div>
                <div class="text-[11px] text-slate-500">
                  {{ t("account_number") || "Account number" }}:
                  {{ selected.bank_account.account_number }}
                </div>
              </div>
              <div v-else class="text-[11px] text-slate-500">—</div>

              <div class="grid grid-cols-1 gap-2 mt-3 sm:grid-cols-2">
                <div>
                  <div class="text-[11px] text-slate-500">
                    {{ t("account_identifier") || "Account Identifier" }}
                  </div>
                  <div
                    class="text-sm font-semibold text-slate-900 dark:text-white"
                  >
                    {{ selected?.account_identifier || "—" }}
                  </div>
                </div>
                <div>
                  <div class="text-[11px] text-slate-500">
                    {{ t("transaction_reference") || "Transaction Reference" }}
                  </div>
                  <div
                    class="text-sm font-semibold text-slate-900 dark:text-white"
                  >
                    {{ selected?.transaction_reference || "—" }}
                  </div>
                </div>
              </div>

              <div v-if="selected?.proof_image_url" class="mt-4">
                <button
                  type="button"
                  class="inline-flex items-center justify-center h-10 gap-2 px-4 text-xs font-semibold text-white rounded-xl bg-primary hover:bg-primary/90"
                  @click="openUrl(selected.proof_image_url)"
                >
                  <i class="mdi mdi-image-outline text-[16px]"></i>
                  {{ t("view_payment_image") || "View payment image" }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- footer -->
        <div
          class="flex justify-end gap-2 p-4 border-t border-slate-200 dark:border-slate-700"
        >
          <button
            type="button"
            class="inline-flex items-center justify-center h-10 gap-2 px-4 text-xs font-semibold border rounded-xl border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/70 hover:bg-slate-50 dark:hover:bg-slate-800"
            @click="closeModal"
          >
            {{ t("close") || "Close" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
import { messages } from "@/i18n";
import api from "@/services/api";
import AdminSidebar from "@/components/Auth/Sidebar.vue";
import AdminTopbar from "@/components/Auth/TopNav.vue";

/* i18n */
const initialLang = (
  document.documentElement.lang ||
  localStorage.getItem("app_lang") ||
  "ar"
).toLowerCase();
const lang = ref(initialLang.startsWith("ar") ? "ar" : "en");
function t(key) {
  return (messages[lang.value] && messages[lang.value][key]) || key;
}

/* layout */
const isSidebarCollapsed = ref(false);
const isSidebarOpenMobile = ref(false);
const sidebarSearch = ref("");
function toggleSidebar() {
  if (window.innerWidth < 768)
    isSidebarOpenMobile.value = !isSidebarOpenMobile.value;
  else isSidebarCollapsed.value = !isSidebarCollapsed.value;
}

/* state */
const loading = ref(false);
const invoices = ref([]);
const filters = reactive({ status: "", q: "" });

/* modal */
const showModal = ref(false);
const selected = ref(null);
function openInvoiceModal(inv) {
  selected.value = inv;
  showModal.value = true;
}
function closeModal() {
  showModal.value = false;
  selected.value = null;
}
function openUrl(url) {
  if (!url) return;
  window.open(url, "_blank");
}

/* helpers */
function statusLabel(st) {
  const v = String(st || "").toLowerCase();
  if (v === "paid") return t("paid") || "Paid";
  if (v === "unpaid") return t("pending") || "Pending"; // reuse existing key
  if (v === "cancelled") return t("reject") || "Cancelled"; // reuse existing key
  return st || "—";
}

function statusChip(st) {
  const v = String(st || "").toLowerCase();
  if (v === "paid")
    return "bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300";
  if (v === "unpaid")
    return "bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300";
  if (v === "cancelled")
    return "bg-slate-200/60 dark:bg-slate-800 text-slate-700 dark:text-slate-200";
  return "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200";
}

const filteredInvoices = computed(() => {
  const q = String(filters.q || "")
    .trim()
    .toLowerCase();

  return invoices.value.filter((x) => {
    if (
      filters.status &&
      String(x.status || "").toLowerCase() !== filters.status
    )
      return false;

    if (q) {
      const hay = [
        x.number,
        x.client_name,
        x.client_phone,
        x.client_email,
        x.consultant_name,
        x.id,
      ]
        .map((z) => String(z || "").toLowerCase())
        .join(" ");
      if (!hay.includes(q)) return false;
    }
    return true;
  });
});

const paidCount = computed(
  () =>
    invoices.value.filter((i) => String(i.status).toLowerCase() === "paid")
      .length
);
const unpaidCount = computed(
  () =>
    invoices.value.filter((i) => String(i.status).toLowerCase() === "unpaid")
      .length
);

const meta = ref(null);
const links = ref(null);
const summary = ref(null);
/* API (غيّر endpoint حسب مشروعك) */
async function fetchInvoices(pageUrl = null) {
  loading.value = true;
  try {
    const res = pageUrl
      ? await api.get(pageUrl)
      : await api.get("/my-invoices");

    const list = res?.data?.data || [];
    meta.value = res?.data?.meta || null;
    links.value = res?.data?.links || null;
    summary.value = res?.data?.summary || null;

    invoices.value = (Array.isArray(list) ? list : []).map((x) => ({
      id: x.id,
      number: x.invoice_number,
      issued_at: x.issued_at,
      due_at: x.due_at,
      paid_at: x.paid_at,

      client_name: x.client_name,
      client_phone: x.client_phone,
      client_email: x.client_email,
      consultant_name: x.consultant_name,

      subtotal: Number(x.subtotal || 0),
      tax_percent: Number(x.tax_rate || 0),
      tax_amount: Number(x.tax_amount || 0),
      total: Number(x.total || 0),

      status: x.status, // paid/unpaid/cancelled ... حسب نظامك
    }));
  } catch (e) {
    console.log("MY INVOICES ERROR:", e?.response?.status, e?.response?.data);
    invoices.value = [];
    meta.value = links.value = summary.value = null;
  } finally {
    loading.value = false;
  }
}
/* keep lang synced (مثل صفحتك) */
const _langObserver = ref(null);
const _langHandler = (e) => {
  const next = (e.detail || "ar").toLowerCase().startsWith("en") ? "en" : "ar";
  if (next !== lang.value) lang.value = next;
};
window.addEventListener("app:lang", _langHandler, { passive: true });

_langObserver.value = new MutationObserver(() => {
  const htmlLang = (
    document.documentElement.lang ||
    localStorage.getItem("app_lang") ||
    "ar"
  ).toLowerCase();
  const next = htmlLang.startsWith("en") ? "en" : "ar";
  if (next !== lang.value) lang.value = next;
});
_langObserver.value.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ["lang", "dir"],
});

onMounted(async () => {
  await fetchInvoices();
});

onBeforeUnmount(() => {
  if (_langObserver.value) _langObserver.value.disconnect();
  window.removeEventListener("app:lang", _langHandler);
});
</script>
